/**
 * Post-test probability by Bayes' theorem in odds form (Fagan 1975):
 *
 *   pre-test odds = p / (1 − p),   post-test odds = pre-test odds × LR,
 *   post-test probability = odds / (1 + odds).
 *
 * Likelihood ratios from sensitivity and specificity: LR+ = Se / (1 − Sp) and
 * LR− = (1 − Se) / Sp. Several tests are chained by multiplying their likelihood ratios,
 * which is valid only if the tests are conditionally independent given the true disease
 * status — the calculator says so wherever it chains.
 *
 * Infinite and zero likelihood ratios are legal (a specificity of 100% makes LR+ infinite);
 * they give post-test probabilities of exactly 1 and 0.
 */

export function oddsFromProbability(probability: number): number {
  if (probability >= 1) {
    return Number.POSITIVE_INFINITY;
  }
  return probability / (1 - probability);
}

export function probabilityFromOdds(odds: number): number {
  if (odds === Number.POSITIVE_INFINITY) {
    return 1;
  }
  return odds / (1 + odds);
}

export interface LikelihoodRatios {
  positive: number;
  negative: number;
}

/** LR+ and LR− from sensitivity and specificity, both as fractions in [0, 1]. */
export function likelihoodRatios(
  sensitivity: number,
  specificity: number
): LikelihoodRatios {
  return {
    positive: ratioOver(sensitivity, 1 - specificity),
    negative: ratioOver(1 - sensitivity, specificity),
  };
}

/** numerator / denominator with 0/0 undefined and x/0 infinite. */
function ratioOver(numerator: number, denominator: number): number {
  if (denominator === 0) {
    return numerator > 0 ? Number.POSITIVE_INFINITY : Number.NaN;
  }
  return numerator / denominator;
}

export interface ChainStep {
  lr: number;
  oddsBefore: number;
  oddsAfter: number;
  probabilityAfter: number;
}

export interface ChainResult {
  preTestProbability: number;
  preTestOdds: number;
  steps: ChainStep[];
  combinedLr: number;
  postTestOdds: number;
  postTestProbability: number;
}

/** Apply likelihood ratios in order. Null if the combination is contradictory (0 × ∞). */
export function chainTests(
  preTestProbability: number,
  ratios: readonly number[]
): ChainResult | null {
  const preTestOdds = oddsFromProbability(preTestProbability);
  const steps: ChainStep[] = [];
  let odds = preTestOdds;
  let combined = 1;
  for (const lr of ratios) {
    const next = odds * lr;
    if (Number.isNaN(next)) {
      return null;
    }
    steps.push({
      lr,
      oddsBefore: odds,
      oddsAfter: next,
      probabilityAfter: probabilityFromOdds(next),
    });
    odds = next;
    combined *= lr;
  }
  if (Number.isNaN(combined)) {
    return null;
  }
  return {
    preTestProbability,
    preTestOdds,
    steps,
    combinedLr: combined,
    postTestOdds: odds,
    postTestProbability: probabilityFromOdds(odds),
  };
}

/** The natural-frequency reading: of 100 people with this result, about this many have it. */
export function perHundred(probability: number): number {
  return Math.round(probability * 100);
}

/**
 * Vertical positions on a Fagan nomogram, in "log-odds units". The outer scales are log
 * odds (the left one inverted) and the middle scale is half of ln LR, so a straight line
 * from the pre-test point through the likelihood ratio lands on the post-test
 * probability: y_LR = (y_post + y_pre) / 2 with y_pre = −ln(odds_pre), y_post = ln(odds_post).
 */
export function nomogramPositions(
  preTestProbability: number,
  likelihoodRatio: number
): { pre: number; middle: number; post: number } {
  const clamp = (p: number) => Math.min(0.9999, Math.max(0.0001, p));
  const pre = -Math.log(oddsFromProbability(clamp(preTestProbability)));
  const postProbability = clamp(
    probabilityFromOdds(
      oddsFromProbability(preTestProbability) * likelihoodRatio
    )
  );
  const post = Math.log(oddsFromProbability(postProbability));
  return { pre, middle: (pre + post) / 2, post };
}
